import React, { useEffect } from "react"
import { Tuple } from "components/Shared/Interfaces"
import useFetchFilters from "components/Shared/GlobalFilter/hooks"
import {
	VENDORS_FILTER,
	CATEGORY_FILTER,
	SUBCATEGORY_FILTER,
	DEPARTMENT_FILTER,
	VENDORS,
	DEPARTMENTS,
	CATEGORIES,
	SUBCATEGORIES,
	VENDORS_FILTER_ENDPOINT,
	DEPARTMENTS_FILTER_ENDPOINT,
	CATEGORIES_FILTER_ENDPOINT,
	SUBCATEGORIES_FILTER_ENDPOINT,
	FINELINES_FILTER_ENDPOINT,
	FINELINE_FILTER,
	FINELINE,
} from "components/Shared/GlobalFilter/constants"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { capitalizeTuple } from "components/Shared/Filters/utils"
import Select from "components/Shared/Filters/Select"
import { useApplicationContext } from "context/applicationContext"
import { ALL_CODE, defaultTuple } from "helpers/service"
import { disabledConditions } from "components/Shared/Filters/modules/utils"
import { OnlyWalmartAvailable } from "components/Shared/Filters/Wrappers"

export const DEFAULT_PROPS: Record<string, FilterProps> = {
	[VENDORS]: {
		isMulti: true,
		allOption: true,
	},
	[DEPARTMENTS]: {
		isMulti: true,
		allOption: true,
	},
	[CATEGORIES]: {
		isMulti: true,
		allOption: true,
	},
	[SUBCATEGORIES]: {
		isMulti: true,
		allOption: true,
	},
}

interface FilterProps {
	isMulti: boolean
	allOption: boolean
	isClearable?: boolean
	initialValue?: boolean
}

interface SelectorsProps {
	selectProps?: Record<string, FilterProps>
	onChange?: (
		filterId: string,
		selectedFilters: Array<Tuple>,
		filterLevelSelected: string
	) => void
}

const Selectors: React.FC<SelectorsProps> = ({
	selectProps = DEFAULT_PROPS,
	onChange = () => undefined,
}) => {
	const selectKey = "filters-select-"
	const { filters, setFilters } = useFiltersContext()
	const { isSupplier } = useApplicationContext()

	const {
		data: vendors,
		refetch: fetchVendors,
		isFetching: isFetchingVendors,
	} = useFetchFilters<Tuple>(
		VENDORS_FILTER_ENDPOINT,
		VENDORS_FILTER,
		undefined,
		true
	)

	const {
		data: departments,
		refetch: fetchDepartments,
		isFetching: isFetchingDepartments,
	} = useFetchFilters<Tuple>(
		DEPARTMENTS_FILTER_ENDPOINT,
		DEPARTMENT_FILTER,
		{ [VENDORS]: filters.vendors },
		true
	)

	const {
		data: categories,
		refetch: fetchCategories,
		isFetching: isFetchingCategories,
	} = useFetchFilters<Tuple>(
		CATEGORIES_FILTER_ENDPOINT,
		CATEGORY_FILTER,
		{
			[VENDORS]: filters.vendors,
			[DEPARTMENTS]: filters.departments,
		},
		true
	)

	const {
		data: subCategories,
		refetch: fetchSubCategories,
		isFetching: isFetchingSubCategories,
	} = useFetchFilters<Tuple>(
		SUBCATEGORIES_FILTER_ENDPOINT,
		SUBCATEGORY_FILTER,
		{
			[VENDORS]: filters.vendors,
			[DEPARTMENTS]: filters.departments,
			[CATEGORIES]: filters.categories,
		},
		true
	)

	const {
		data: fineline,
		refetch: fetchFineline,
		isFetching: isFetchingFineline,
	} = useFetchFilters<Tuple>(
		FINELINES_FILTER_ENDPOINT,
		FINELINE_FILTER,
		{
			[VENDORS]: filters.vendors,
			[DEPARTMENTS]: filters.departments,
			[CATEGORIES]: filters.categories,
			[SUBCATEGORIES]: filters.subcategories,
		},
		true
	)

	useEffect(() => {
		if (isFetchingVendors) return
		fetchDepartments()
		if (!isSupplier) {
			fetchVendors()
		}
	}, [])

	useEffect(() => {
		if (isFetchingVendors || filters.vendors.length === 0) return

		fetchDepartments()
	}, [filters.vendors])

	useEffect(() => {
		if (isFetchingDepartments) return

		filters.departments?.length && fetchCategories()
	}, [filters.departments])

	useEffect(() => {
		if (isFetchingCategories) return

		filters.categories?.length && fetchSubCategories()
	}, [filters.categories])

	useEffect(() => {
		if (isFetchingSubCategories) return

		filters.subcategories?.length && fetchFineline()
	}, [filters.subcategories])

	const handleFilters = (
		filterId: string,
		selectedFilters: Array<Tuple>,
		dependencies?: Record<string, Array<Tuple>>
	) => {
		const hasAllCodeSelected = selectedFilters.some(
			({ code }) => code === ALL_CODE
		)

		const filterChanged = hasAllCodeSelected ? [defaultTuple] : selectedFilters

		setFilters({
			...filters,
			[filterId]: filterChanged,
			...dependencies,
		})

		onChange(filterId, filterChanged, filterId)
	}

	return (
		<React.Fragment>
			<OnlyWalmartAvailable>
				<Select
					key={selectKey + VENDORS}
					options={capitalizeTuple(vendors)}
					id={VENDORS}
					onChange={(value: Array<Tuple>) =>
						handleFilters(VENDORS, value, {
							[DEPARTMENTS]: [],
							[CATEGORIES]: [],
							[SUBCATEGORIES]: [],
							[FINELINE]: [],
						})
					}
					disabledText={VENDORS}
					isLoading={isFetchingVendors}
					allOption={selectProps.vendors?.allOption}
					isMulti={selectProps.vendors?.isMulti}
					initialValue={selectProps.vendors?.initialValue}
					isClearable={selectProps.vendors?.isClearable}
					isVisible={!!selectProps.vendors}
				/>
			</OnlyWalmartAvailable>
			<Select
				id={DEPARTMENTS}
				key={selectKey + DEPARTMENTS}
				options={capitalizeTuple(departments)}
				onChange={(value: Array<Tuple>) =>
					handleFilters(DEPARTMENTS, value, {
						[CATEGORIES]: [],
						[SUBCATEGORIES]: [],
						[FINELINE]: [],
					})
				}
				disabledText={VENDORS}
				isLoading={isFetchingDepartments}
				allOption={selectProps.departments?.allOption}
				initialValue={selectProps.departments?.initialValue}
				isMulti={selectProps.departments?.isMulti}
				isClearable={selectProps.departments?.isClearable}
				isVisible={!!selectProps.departments}
			/>
			<Select
				key={selectKey + CATEGORIES}
				id={CATEGORIES}
				options={categories}
				onChange={(value: Tuple[]) =>
					handleFilters(CATEGORIES, value, {
						[SUBCATEGORIES]: [],
						[FINELINE]: [],
					})
				}
				disabledText={DEPARTMENTS}
				isLoading={isFetchingCategories}
				isDisabled={disabledConditions([
					isFetchingDepartments,
					!filters.departments?.length,
				])}
				isMulti={selectProps.categories?.isMulti}
				allOption={selectProps.categories?.allOption}
				initialValue={selectProps.categories?.initialValue}
				isClearable={selectProps.categories?.isClearable}
				isVisible={!!selectProps.categories}
			/>
			<Select
				id={SUBCATEGORIES}
				key={selectKey + SUBCATEGORIES}
				options={subCategories}
				onChange={(value: Tuple[]) =>
					handleFilters(SUBCATEGORIES, value, { [FINELINE]: [] })
				}
				disabledText={CATEGORIES}
				isLoading={isFetchingSubCategories}
				isDisabled={disabledConditions([
					isFetchingSubCategories,
					!filters.categories?.length,
				])}
				isMulti={selectProps.subcategories?.isMulti}
				allOption={selectProps.subcategories?.allOption}
				initialValue={selectProps.subcategories?.initialValue}
				isClearable={selectProps.subcategories?.isClearable}
				isVisible={!!selectProps.subcategories}
			/>
			<Select
				id={FINELINE}
				key={selectKey + FINELINE}
				options={fineline}
				onChange={(value: Tuple[]) => handleFilters(FINELINE, value)}
				disabledText={SUBCATEGORIES}
				isLoading={isFetchingFineline}
				isDisabled={disabledConditions([
					isFetchingFineline,
					!filters.subcategories?.length,
				])}
				allOption={selectProps.fineline?.allOption}
				initialValue={selectProps.fineline?.initialValue}
				isMulti={selectProps.fineline?.isMulti}
				isClearable={selectProps.fineline?.isClearable}
				isVisible={!!selectProps.fineline}
			/>
		</React.Fragment>
	)
}

export default Selectors
